import React from 'react';
import {
  Box, Grid, Typography, Paper, TextField, MenuItem, FormControlLabel, Switch, Chip, Breadcrumbs, Button, Snackbar, Alert, InputAdornment,
} from '@mui/material';
import { Link } from 'react-router-dom';
import HistoryIcon from '@mui/icons-material/History';
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCorners, DragOverlay } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import { SaveBar, SkeletonCards, ErrorState, PermissionState } from '../../ui';
import FeatureLibrary from './FeatureLibrary';
import PlanCanvas from './PlanCanvas';
import PlanReviewDialog from './PlanReviewDialog';
import PlanVersionsDrawer from './PlanVersionsDrawer';
import useGAPlanBuilderPage from './useGAPlanBuilderPage';
import { LIB_PREFIX } from './planUtils';

const GAPlanBuilder = () => {
  const p = useGAPlanBuilderPage();
  const [activeDrag, setActiveDrag] = React.useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (p.status === 'loading' || !p.draft) return <SkeletonCards rows={6} />;
  if (p.status === 'error') {
    return <ErrorState title={p.t("Couldn't load this plan", { defaultValue: "Couldn't load this plan" })} request={p.errorInfo?.request} retryLabel={p.t('Try again', { defaultValue: 'Try again' })} onRetry={p.load} />;
  }
  if (p.status === 'permission') return <PermissionState title={p.t('ga.error.forbidden')} />;

  const dragLabel = activeDrag ? (p.featuresByKey[String(activeDrag).replace(LIB_PREFIX, '')]?.name || '') : '';

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1.5 }}>
        <Link to="/ga/plans" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Typography color="text.secondary" variant="body2">{p.t('Plans & Pricing', { defaultValue: 'Plans & Pricing' })}</Typography>
        </Link>
        <Typography variant="body2">{p.plan.name}</Typography>
      </Breadcrumbs>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            label={p.t('Plan name', { defaultValue: 'Plan name' })}
            value={p.draft.name}
            onChange={(e) => p.setField('name', e.target.value)}
            error={Boolean(p.errors.name)}
            helperText={p.errors.name}
            inputProps={{ maxLength: 40 }}
            sx={{ minWidth: 220 }}
          />
          <TextField
            label={p.t('Monthly price', { defaultValue: 'Monthly price' })}
            value={p.priceInput}
            onChange={(e) => p.setPriceInput(e.target.value)}
            error={Boolean(p.errors.price)}
            helperText={p.errors.price}
            inputProps={{ inputMode: 'decimal', 'aria-label': p.t('Monthly price', { defaultValue: 'Monthly price' }) }}
            InputProps={{ endAdornment: <InputAdornment position="end">/ {p.t('month', { defaultValue: 'month' })}</InputAdornment> }}
            sx={{ width: 180 }}
          />
          <TextField select label={p.t('Currency', { defaultValue: 'Currency' })} value={p.draft.price.currency} onChange={(e) => p.setPrice({ currency: e.target.value })} sx={{ width: 110 }}>
            {['USD', 'CAD', 'EUR', 'GBP', 'TRY'].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={<Switch checked={p.draft.highlight} onChange={(e) => p.setField('highlight', e.target.checked)} />}
            label={p.t('Highlight as “Most popular”', { defaultValue: 'Highlight as “Most popular”' })}
            sx={{ alignSelf: 'center' }}
          />
        </Box>
        <TextField
          fullWidth
          label={p.t('Description', { defaultValue: 'Description' })}
          value={p.draft.description}
          onChange={(e) => p.setField('description', e.target.value)}
          error={Boolean(p.errors.description)}
          helperText={p.errors.description}
          inputProps={{ maxLength: 160 }}
          sx={{ mt: 2 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          {p.plan.visibility === 'private' && <Chip size="small" color="secondary" variant="outlined" label={p.t('Operator-only · never shown to companies', { defaultValue: 'Operator-only · never shown to companies' })} />}
          {p.plan.isDefault && <Chip size="small" color="success" variant="outlined" label={p.t('Default', { defaultValue: 'Default' })} />}
          <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {p.t('{{count}} companies on this plan', { count: p.plan.subscriberCount ?? 0, defaultValue: `${p.plan.subscriberCount ?? 0} companies on this plan` })}
            {' · '}v{p.plan.currentVersion}
            {p.plan.updatedAt ? ` · ${p.t('edited', { defaultValue: 'edited' })} ${new Date(p.plan.updatedAt).toLocaleString()}` : ''}
          </Typography>
          <Button size="small" variant="text" startIcon={<HistoryIcon fontSize="small" />} onClick={p.openVersions} sx={{ ml: 'auto' }}>
            {p.t('Version history', { defaultValue: 'Version history' })}
          </Button>
        </Box>
      </Paper>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={({ active }) => setActiveDrag(active.id)}
        onDragEnd={(e) => { setActiveDrag(null); p.handleDragEnd(e); }}
        onDragCancel={() => setActiveDrag(null)}
      >
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={4} lg={3}>
            <FeatureLibrary features={p.features} inPlanKeys={p.inPlanKeys} onAdd={p.addFeature} t={p.t} />
          </Grid>
          <Grid item xs={12} md={8} lg={9}>
            <PlanCanvas
              planName={p.draft.name || p.plan.name}
              entitlements={p.draft.entitlements}
              featuresByKey={p.featuresByKey}
              usageStats={p.plan.usageStats}
              errors={p.errors}
              onChange={p.changeEntitlement}
              onRemove={p.removeFeature}
              t={p.t}
            />
          </Grid>
        </Grid>
        <DragOverlay>
          {dragLabel ? <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, borderColor: 'primary.main' }}><Typography variant="body2">{dragLabel}</Typography></Paper> : null}
        </DragOverlay>
      </DndContext>

      <SaveBar
        dirty={p.isDirty}
        saving={p.saving}
        message={p.isValid
          ? p.t('{{count}} unsaved changes · affects {{companies}} companies', { count: p.changes.length, companies: p.plan.subscriberCount ?? 0, defaultValue: `${p.changes.length} unsaved changes · affects ${p.plan.subscriberCount ?? 0} companies` })
          : p.t('Fix the highlighted fields to save', { defaultValue: 'Fix the highlighted fields to save' })}
        discardLabel={p.t('Discard', { defaultValue: 'Discard' })}
        saveLabel={p.t('Review & save', { defaultValue: 'Review & save' })}
        onDiscard={p.discard}
        onSave={p.openReview}
      />

      <PlanReviewDialog
        open={p.reviewOpen}
        onClose={() => p.setReviewOpen(false)}
        onConfirm={p.save}
        loading={p.saving}
        impact={p.impact}
        impactLoading={p.impactLoading}
        planName={p.draft.name}
        nextVersion={p.plan.currentVersion + 1}
        t={p.t}
      />

      <PlanVersionsDrawer
        open={p.versionsOpen}
        onClose={() => p.setVersionsOpen(false)}
        versions={p.versions}
        loading={p.versionsLoading}
        currentVersion={p.plan.currentVersion}
        onRestore={p.restoreVersion}
        t={p.t}
      />

      <Snackbar open={p.snackbar.open} autoHideDuration={5000} onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>{p.snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default GAPlanBuilder;
